import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';
import { Chips } from 'primereact/chips';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toolbar } from 'primereact/toolbar';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Editor } from 'primereact/editor';
import axiosInstance from '../../config/axios.config';

const StandardDocuments = () => {
    const toast = useRef(null);
    const fileUploadRef = useRef(null);
    const dt = useRef(null);
    
    // State management
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documentDialog, setDocumentDialog] = useState(false);
    const [deleteDocumentDialog, setDeleteDocumentDialog] = useState(false);
    const [deleteDocumentsDialog, setDeleteDocumentsDialog] = useState(false);
    const [document, setDocument] = useState(null);
    const [selectedDocuments, setSelectedDocuments] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [categories, setCategories] = useState({});
    const [documentTypes, setDocumentTypes] = useState({});
    const [loanTypes, setLoanTypes] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    
    // Filter states
    const [selectedCategories, setSelectedCategories] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState(null);
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState(null);
    
    // Document statuses
    const statuses = [
        { label: 'Draft', value: 'draft' },
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Archived', value: 'archived' }
    ];

    useEffect(() => {
        // Delay initial load to ensure refs are properly initialized
        const timer = setTimeout(() => {
            loadDocuments();
            loadCategories();
            loadDocumentTypes();
            loadLoanTypes();
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    const loadDocuments = async () => {
        try {
            setLoading(true);
            console.log('Loading standard documents...');
            
            // Fetch with higher per_page to get all documents
            const response = await axiosInstance.get('/standard-documents', {
                params: {
                    per_page: 100 // Get up to 100 documents
                }
            });
            
            console.log('API Response:', response.data);
            
            if (response.data.success) {
                const docs = response.data.data || [];
                console.log(`Loaded ${docs.length} documents`);
                setDocuments(docs);
                
                // If no documents, show info message
                if (docs.length === 0 && toast.current) {
                    toast.current.show({
                        severity: 'info',
                        summary: 'No Documents',
                        detail: 'No standard documents found. Click "New" to create one.',
                        life: 5000
                    });
                }
            } else {
                console.warn('API returned success=false:', response.data);
                setDocuments([]);
            }
        } catch (error) {
            console.error('Error loading documents:', error);
            console.error('Error response:', error.response);
            
            const errorMessage = error.response?.data?.message || 
                                error.response?.status === 401 ? 'Authentication required' :
                                error.response?.status === 404 ? 'API endpoint not found' :
                                error.message || 'Failed to load documents';
            
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage,
                    life: 5000
                });
            }
            setDocuments([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await axiosInstance.get('/standard-documents/categories');
            if (response.data.success) {
                setCategories(response.data.data || {});
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            // Set default categories if API fails
            setCategories({
                'agreement': 'Agreement',
                'disclosure': 'Disclosure',
                'verification': 'Verification',
                'consent': 'Consent',
                'declaration': 'Declaration',
                'undertaking': 'Undertaking',
                'noc': 'NOC',
                'terms': 'Terms',
                'policy': 'Policy',
                'other': 'Other'
            });
        }
    };

    const loadDocumentTypes = async () => {
        try {
            const response = await axiosInstance.get('/standard-documents/document-types');
            if (response.data.success) {
                setDocumentTypes(response.data.data || {});
            }
        } catch (error) {
            console.error('Error loading document types:', error);
            // Set default document types if API fails
            setDocumentTypes({
                'template': 'Template',
                'sample': 'Sample',
                'mandatory': 'Mandatory',
                'optional': 'Optional',
                'reference': 'Reference'
            });
        }
    };

    const loadLoanTypes = async () => {
        try {
            const response = await axiosInstance.get('/loan-types');
            if (response.data.success && response.data.data) {
                const loanTypeOptions = response.data.data.map(lt => ({
                    label: lt.name,
                    value: lt.id
                }));
                setLoanTypes(loanTypeOptions);
            }
        } catch (error) {
            console.error('Error loading loan types:', error);
            setLoanTypes([]);
        }
    };

    const openNew = () => {
        setDocument({
            name: '',
            code: '',
            description: '',
            category: 'agreement',
            document_type: 'template',
            loan_type_id: null,
            applies_to_all_loans: false,
            content: '',
            template_variables: [],
            effective_from: new Date(),
            effective_to: null,
            required_fields: [],
            applicability_rules: [],
            display_order: 0,
            language: 'en',
            regulatory_reference: '',
            requires_signature: false,
            requires_witness: false,
            requires_notarization: false,
            status: 'draft',
            tags: [],
            notes: ''
        });
        setSubmitted(false);
        setDocumentDialog(true);
        setActiveTabIndex(0);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setDocumentDialog(false);
        setUploadedFile(null);
    };

    const hideDeleteDocumentDialog = () => {
        setDeleteDocumentDialog(false);
    };

    const hideDeleteDocumentsDialog = () => {
        setDeleteDocumentsDialog(false);
    };

    const saveDocument = async () => {
        setSubmitted(true);

        if (document.name.trim()) {
            try {
                const formData = new FormData();
                
                // Add all document fields to formData
                Object.keys(document).forEach(key => {
                    if (document[key] !== null && document[key] !== undefined) {
                        if (key === 'effective_from' || key === 'effective_to') {
                            // Format dates
                            if (document[key]) {
                                formData.append(key, new Date(document[key]).toISOString().split('T')[0]);
                            }
                        } else if (Array.isArray(document[key]) || typeof document[key] === 'object') {
                            formData.append(key, JSON.stringify(document[key]));
                        } else {
                            formData.append(key, document[key]);
                        }
                    }
                });

                // Add file if uploaded
                if (uploadedFile) {
                    formData.append('file', uploadedFile);
                }

                let response;
                if (document.id) {
                    // Update existing document
                    response = await axiosInstance.put(`/standard-documents/${document.id}`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                } else {
                    // Create new document
                    response = await axiosInstance.post('/standard-documents', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                }

                if (response.data.success) {
                    if (toast.current) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: document.id ? 'Document updated successfully' : 'Document created successfully',
                            life: 3000
                        });
                    }
                    loadDocuments();
                    setDocumentDialog(false);
                    setDocument(null);
                    setUploadedFile(null);
                }
            } catch (error) {
                console.error('Error saving document:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to save document',
                        life: 3000
                    });
                }
            }
        }
    };

    const editDocument = (document) => {
        setDocument({ ...document });
        setDocumentDialog(true);
        setActiveTabIndex(0);
    };

    const confirmDeleteDocument = (document) => {
        setDocument(document);
        setDeleteDocumentDialog(true);
    };

    const deleteDocument = async () => {
        try {
            const response = await axiosInstance.delete(`/standard-documents/${document.id}`);
            if (response.data.success) {
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Document deleted successfully',
                        life: 3000
                    });
                }
                loadDocuments();
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete document',
                    life: 3000
                });
            }
        }
        setDeleteDocumentDialog(false);
        setDocument(null);
    };

    const confirmDeleteSelected = () => {
        setDeleteDocumentsDialog(true);
    };

    const deleteSelectedDocuments = async () => {
        try {
            // Delete documents one by one (you could also implement a bulk delete endpoint)
            for (const doc of selectedDocuments) {
                await axiosInstance.delete(`/standard-documents/${doc.id}`);
            }
            
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Documents deleted successfully',
                    life: 3000
                });
            }
            loadDocuments();
            setSelectedDocuments(null);
        } catch (error) {
            console.error('Error deleting documents:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete documents',
                    life: 3000
                });
            }
        }
        setDeleteDocumentsDialog(false);
    };

    const activateDocument = async (documentId) => {
        try {
            const response = await axiosInstance.post(`/standard-documents/${documentId}/activate`);
            if (response.data.success) {
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Document activated successfully',
                        life: 3000
                    });
                }
                loadDocuments();
            }
        } catch (error) {
            console.error('Error activating document:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to activate document',
                    life: 3000
                });
            }
        }
    };

    const createVersion = async (documentId) => {
        confirmDialog({
            message: 'Are you sure you want to create a new version of this document?',
            header: 'Create New Version',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const response = await axiosInstance.post(`/standard-documents/${documentId}/version`);
                    if (response.data.success) {
                        if (toast.current) {
                            toast.current.show({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'New version created successfully',
                                life: 3000
                            });
                        }
                        loadDocuments();
                    }
                } catch (error) {
                    console.error('Error creating version:', error);
                    if (toast.current) {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to create new version',
                            life: 3000
                        });
                    }
                }
            }
        });
    };

    const downloadDocument = async (documentId, fileName) => {
        try {
            const response = await axiosInstance.get(`/standard-documents/${documentId}/download`, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || 'document.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Document downloaded successfully',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error downloading document:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to download document',
                    life: 3000
                });
            }
        }
    };

    const onUpload = (event) => {
        setUploadedFile(event.files[0]);
        if (toast.current) {
            toast.current.show({
                severity: 'info',
                summary: 'File Selected',
                detail: 'File ready for upload',
                life: 3000
            });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _document = { ...document };
        _document[`${name}`] = val;
        setDocument(_document);
    };

    const onCheckboxChange = (e, name) => {
        let _document = { ...document };
        _document[`${name}`] = e.checked;
        setDocument(_document);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    // Column templates
    const nameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="font-bold">{rowData.name}</div>
                <div className="text-sm text-500">{rowData.code}</div>
            </div>
        );
    };

    const categoryBodyTemplate = (rowData) => {
        return <Tag value={categories[rowData.category] || rowData.category} severity="info" />;
    };

    const documentTypeBodyTemplate = (rowData) => {
        const severity = {
            'template': 'success',
            'sample': 'info',
            'mandatory': 'danger',
            'optional': 'warning',
            'reference': null
        };
        return <Tag value={documentTypes[rowData.document_type] || rowData.document_type} severity={severity[rowData.document_type]} />;
    };

    const statusBodyTemplate = (rowData) => {
        const severity = {
            'draft': 'warning',
            'active': 'success',
            'inactive': 'danger',
            'archived': null
        };
        return <Tag value={rowData.status} severity={severity[rowData.status]} />;
    };

    const loanTypeBodyTemplate = (rowData) => {
        if (rowData.applies_to_all_loans) {
            return <Badge value="All Loans" severity="success" />;
        }
        return rowData.loan_type?.name || '-';
    };

    const effectiveDateBodyTemplate = (rowData) => {
        const from = new Date(rowData.effective_from).toLocaleDateString();
        const to = rowData.effective_to ? new Date(rowData.effective_to).toLocaleDateString() : 'No End Date';
        return `${from} - ${to}`;
    };

    const requirementsBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                {rowData.requires_signature && <Tag value="Signature" />}
                {rowData.requires_witness && <Tag value="Witness" />}
                {rowData.requires_notarization && <Tag value="Notarization" />}
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-pencil" aria-label="Edit" rounded text severity="success" onClick={() => editDocument(rowData)} tooltip="Edit" />
                {rowData.file_path && (
                    <Button icon="pi pi-download" aria-label="Download" rounded text severity="info" onClick={() => downloadDocument(rowData.id, rowData.file_name)} tooltip="Download" />
                )}
                {rowData.status === 'draft' && (
                    <Button icon="pi pi-check" aria-label="Activate" rounded text severity="warning" onClick={() => activateDocument(rowData.id)} tooltip="Activate" />
                )}
                <Button icon="pi pi-copy" aria-label="Create Version" rounded text severity="secondary" onClick={() => createVersion(rowData.id)} tooltip="Create Version" />
                <Button icon="pi pi-trash" aria-label="Delete" rounded text severity="danger" onClick={() => confirmDeleteDocument(rowData)} tooltip="Delete" />
            </div>
        );
    };

    // Toolbar
    const leftToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
                <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={confirmDeleteSelected} disabled={!selectedDocuments || !selectedDocuments.length} />
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
            </div>
        );
    };

    // Header
    const header = (
        <div className="flex flex-column gap-2">
            <div className="flex justify-content-between align-items-center">
                <h5 className="m-0">Standard Documents Management</h5>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
                </span>
            </div>
            <div className="flex gap-2">
                <MultiSelect
                    value={selectedCategories}
                    options={Object.entries(categories).map(([value, label]) => ({ label, value }))}
                    onChange={(e) => setSelectedCategories(e.value)}
                    placeholder="Select Categories"
                    className="w-full md:w-14rem"
                    display="chip"
                />
                <MultiSelect
                    value={selectedDocumentTypes}
                    options={Object.entries(documentTypes).map(([value, label]) => ({ label, value }))}
                    onChange={(e) => setSelectedDocumentTypes(e.value)}
                    placeholder="Select Document Types"
                    className="w-full md:w-14rem"
                    display="chip"
                />
                <MultiSelect
                    value={selectedStatuses}
                    options={statuses}
                    onChange={(e) => setSelectedStatuses(e.value)}
                    placeholder="Select Status"
                    className="w-full md:w-14rem"
                    display="chip"
                />
            </div>
        </div>
    );

    // Document dialog footer
    const documentDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" text onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" onClick={saveDocument} />
        </>
    );

    // Delete dialog footers
    const deleteDocumentDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" text onClick={hideDeleteDocumentDialog} />
            <Button label="Yes" icon="pi pi-check" text onClick={deleteDocument} />
        </>
    );

    const deleteDocumentsDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" text onClick={hideDeleteDocumentsDialog} />
            <Button label="Yes" icon="pi pi-check" text onClick={deleteSelectedDocuments} />
        </>
    );

    // Filter function with debug logging
    const filteredDocuments = documents.filter((doc) => {
        let pass = true;
        
        if (selectedCategories && selectedCategories.length > 0) {
            pass = pass && selectedCategories.includes(doc.category);
        }
        
        if (selectedDocumentTypes && selectedDocumentTypes.length > 0) {
            pass = pass && selectedDocumentTypes.includes(doc.document_type);
        }
        
        if (selectedStatuses && selectedStatuses.length > 0) {
            pass = pass && selectedStatuses.includes(doc.status);
        }
        
        return pass;
    });
    
    // Debug logging
    console.log('Documents count:', documents.length);
    console.log('Filtered documents count:', filteredDocuments.length);
    console.log('Selected filters:', {
        categories: selectedCategories,
        documentTypes: selectedDocumentTypes,
        statuses: selectedStatuses
    });

    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ height: '500px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <ConfirmDialog />
                    
                    <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate} />

                    <DataTable
                        ref={dt}
                        value={filteredDocuments}
                        selection={selectedDocuments}
                        onSelectionChange={(e) => setSelectedDocuments(e.value)}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} documents"
                        globalFilter={globalFilter}
                        header={header}
                        responsiveLayout="scroll"
                    >
                        <Column selectionMode="multiple" exportable={false} />
                        <Column field="name" header="Name" body={nameBodyTemplate} sortable />
                        <Column field="category" header="Category" body={categoryBodyTemplate} sortable />
                        <Column field="document_type" header="Type" body={documentTypeBodyTemplate} sortable />
                        <Column field="loan_type_id" header="Loan Type" body={loanTypeBodyTemplate} sortable />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                        <Column field="effective_from" header="Effective Period" body={effectiveDateBodyTemplate} />
                        <Column field="version" header="Version" sortable />
                        <Column header="Requirements" body={requirementsBodyTemplate} />
                        <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem' }} />
                    </DataTable>

                    <Dialog
                        visible={documentDialog}
                        style={{ width: '900px' }}
                        header="Document Details"
                        modal
                        className="p-fluid"
                        footer={documentDialogFooter}
                        onHide={hideDialog}
                    >
                        <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
                            <TabPanel header="Basic Information">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="name">Name *</label>
                                            <InputText
                                                id="name"
                                                value={document?.name || ''}
                                                onChange={(e) => onInputChange(e, 'name')}
                                                required
                                                autoFocus
                                                className={submitted && !document?.name ? 'p-invalid' : ''}
                                            />
                                            {submitted && !document?.name && <small className="p-error">Name is required.</small>}
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="code">Code</label>
                                            <InputText
                                                id="code"
                                                value={document?.code || ''}
                                                onChange={(e) => onInputChange(e, 'code')}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="description">Description</label>
                                            <InputTextarea
                                                id="description"
                                                value={document?.description || ''}
                                                onChange={(e) => onInputChange(e, 'description')}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="category">Category *</label>
                                            <Dropdown
                                                id="category"
                                                value={document?.category}
                                                options={Object.entries(categories).map(([value, label]) => ({ label, value }))}
                                                onChange={(e) => onInputChange(e, 'category')}
                                                placeholder="Select Category"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="document_type">Document Type *</label>
                                            <Dropdown
                                                id="document_type"
                                                value={document?.document_type}
                                                options={Object.entries(documentTypes).map(([value, label]) => ({ label, value }))}
                                                onChange={(e) => onInputChange(e, 'document_type')}
                                                placeholder="Select Document Type"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="loan_type_id">Loan Type</label>
                                            <Dropdown
                                                id="loan_type_id"
                                                value={document?.loan_type_id}
                                                options={loanTypes}
                                                onChange={(e) => onInputChange(e, 'loan_type_id')}
                                                placeholder="Select Loan Type"
                                                showClear
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field-checkbox">
                                            <Checkbox
                                                inputId="applies_to_all_loans"
                                                checked={document?.applies_to_all_loans || false}
                                                onChange={(e) => onCheckboxChange(e, 'applies_to_all_loans')}
                                            />
                                            <label htmlFor="applies_to_all_loans">Applies to All Loans</label>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="status">Status</label>
                                            <Dropdown
                                                id="status"
                                                value={document?.status}
                                                options={statuses}
                                                onChange={(e) => onInputChange(e, 'status')}
                                                placeholder="Select Status"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="display_order">Display Order</label>
                                            <InputText
                                                id="display_order"
                                                type="number"
                                                value={document?.display_order || 0}
                                                onChange={(e) => onInputChange(e, 'display_order')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel header="Content & Template">
                                <div className="grid">
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="content">Document Content</label>
                                            <Editor
                                                id="content"
                                                value={document?.content || ''}
                                                onTextChange={(e) => {
                                                    let _document = { ...document };
                                                    _document.content = e.htmlValue;
                                                    setDocument(_document);
                                                }}
                                                style={{ height: '320px' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="template_variables">Template Variables</label>
                                            <Chips
                                                id="template_variables"
                                                value={document?.template_variables || []}
                                                onChange={(e) => onInputChange(e, 'template_variables')}
                                                separator=","
                                                placeholder="Add variables (e.g., {{customer_name}}, {{loan_amount}})"
                                            />
                                            <small>Variables that can be replaced in the template</small>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="file">Upload Document File</label>
                                            <FileUpload
                                                ref={fileUploadRef}
                                                name="file"
                                                accept=".pdf,.doc,.docx"
                                                maxFileSize={10485760}
                                                onSelect={onUpload}
                                                auto
                                                chooseLabel="Choose File"
                                                mode="basic"
                                            />
                                            <small>Max file size: 10MB. Accepted formats: PDF, DOC, DOCX</small>
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel header="Validity & Requirements">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="effective_from">Effective From *</label>
                                            <Calendar
                                                id="effective_from"
                                                value={document?.effective_from ? new Date(document.effective_from) : null}
                                                onChange={(e) => onInputChange(e, 'effective_from')}
                                                dateFormat="yy-mm-dd"
                                                showIcon
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="effective_to">Effective To</label>
                                            <Calendar
                                                id="effective_to"
                                                value={document?.effective_to ? new Date(document.effective_to) : null}
                                                onChange={(e) => onInputChange(e, 'effective_to')}
                                                dateFormat="yy-mm-dd"
                                                showIcon
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="version">Version</label>
                                            <InputText
                                                id="version"
                                                value={document?.version || '1.0'}
                                                onChange={(e) => onInputChange(e, 'version')}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="field">
                                            <label htmlFor="regulatory_reference">Regulatory Reference</label>
                                            <InputText
                                                id="regulatory_reference"
                                                value={document?.regulatory_reference || ''}
                                                onChange={(e) => onInputChange(e, 'regulatory_reference')}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label>Requirements</label>
                                            <div className="flex gap-3">
                                                <div className="field-checkbox">
                                                    <Checkbox
                                                        inputId="requires_signature"
                                                        checked={document?.requires_signature || false}
                                                        onChange={(e) => onCheckboxChange(e, 'requires_signature')}
                                                    />
                                                    <label htmlFor="requires_signature">Requires Signature</label>
                                                </div>
                                                <div className="field-checkbox">
                                                    <Checkbox
                                                        inputId="requires_witness"
                                                        checked={document?.requires_witness || false}
                                                        onChange={(e) => onCheckboxChange(e, 'requires_witness')}
                                                    />
                                                    <label htmlFor="requires_witness">Requires Witness</label>
                                                </div>
                                                <div className="field-checkbox">
                                                    <Checkbox
                                                        inputId="requires_notarization"
                                                        checked={document?.requires_notarization || false}
                                                        onChange={(e) => onCheckboxChange(e, 'requires_notarization')}
                                                    />
                                                    <label htmlFor="requires_notarization">Requires Notarization</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="tags">Tags</label>
                                            <Chips
                                                id="tags"
                                                value={document?.tags || []}
                                                onChange={(e) => onInputChange(e, 'tags')}
                                                separator=","
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="notes">Notes</label>
                                            <InputTextarea
                                                id="notes"
                                                value={document?.notes || ''}
                                                onChange={(e) => onInputChange(e, 'notes')}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                        </TabView>
                    </Dialog>

                    <Dialog
                        visible={deleteDocumentDialog}
                        style={{ width: '450px' }}
                        header="Confirm"
                        modal
                        footer={deleteDocumentDialogFooter}
                        onHide={hideDeleteDocumentDialog}
                    >
                        <div className="confirmation-content">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {document && <span>Are you sure you want to delete <b>{document.name}</b>?</span>}
                        </div>
                    </Dialog>

                    <Dialog
                        visible={deleteDocumentsDialog}
                        style={{ width: '450px' }}
                        header="Confirm"
                        modal
                        footer={deleteDocumentsDialogFooter}
                        onHide={hideDeleteDocumentsDialog}
                    >
                        <div className="confirmation-content">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {selectedDocuments && <span>Are you sure you want to delete the selected documents?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default StandardDocuments;